// OAuth 2.1 PKCE client for the workers-oauth-provider server

const LS_CLIENT_ID = "crux:clientId";
const LS_ACCESS_TOKEN = "crux:accessToken";
const LS_REFRESH_TOKEN = "crux:refreshToken";
const LS_EXPIRES_AT = "crux:expiresAt";
const LS_CODE_VERIFIER = "crux:codeVerifier";

function getServerBase(): string {
  // In prod this is the Worker URL; in dev the Vite proxy handles it
  return import.meta.env.VITE_API_BASE ?? "";
}

// --- PKCE helpers ---

function base64UrlEncode(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=/g, "");
}

async function generateCodeVerifier(): Promise<string> {
  const bytes = crypto.getRandomValues(new Uint8Array(48));
  return base64UrlEncode(bytes.buffer);
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(hash);
}

// --- Client registration ---

async function ensureClientId(): Promise<string> {
  const existing = localStorage.getItem(LS_CLIENT_ID);
  if (existing) return existing;

  const redirectUri = `${window.location.origin}/callback`;
  const res = await fetch(`${getServerBase()}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      client_name: "Crux Web",
      redirect_uris: [redirectUri],
      token_endpoint_auth_method: "none",
      grant_types: ["authorization_code", "refresh_token"],
      response_types: ["code"],
    }),
  });

  if (!res.ok) throw new Error(`Client registration failed: ${res.status}`);
  const data = (await res.json()) as { client_id: string };
  localStorage.setItem(LS_CLIENT_ID, data.client_id);
  return data.client_id;
}

// --- Token storage ---

export function getAccessToken(): string | null {
  const token = localStorage.getItem(LS_ACCESS_TOKEN);
  const expiresAt = Number(localStorage.getItem(LS_EXPIRES_AT) ?? 0);
  if (!token) return null;
  if (expiresAt && Date.now() > expiresAt - 60_000) return null; // expired (or expiring soon)
  return token;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(LS_REFRESH_TOKEN);
}

export function clearAuth(): void {
  localStorage.removeItem(LS_ACCESS_TOKEN);
  localStorage.removeItem(LS_REFRESH_TOKEN);
  localStorage.removeItem(LS_EXPIRES_AT);
}

export function isAuthenticated(): boolean {
  return getAccessToken() !== null || getRefreshToken() !== null;
}

function storeTokens(data: { access_token: string; refresh_token?: string; expires_in?: number }): void {
  localStorage.setItem(LS_ACCESS_TOKEN, data.access_token);
  if (data.refresh_token) localStorage.setItem(LS_REFRESH_TOKEN, data.refresh_token);
  if (data.expires_in) {
    localStorage.setItem(LS_EXPIRES_AT, String(Date.now() + data.expires_in * 1000));
  }
}

// --- Auth flow ---

export async function initiateLogin(): Promise<void> {
  const clientId = await ensureClientId();
  const verifier = await generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  const state = crypto.randomUUID();

  sessionStorage.setItem(LS_CODE_VERIFIER, verifier);
  sessionStorage.setItem("crux:oauthState", state);

  const redirectUri = `${window.location.origin}/callback`;
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    state,
    code_challenge: challenge,
    code_challenge_method: "S256",
  });

  window.location.href = `${getServerBase()}/authorize?${params}`;
}

export async function handleCallback(searchParams: URLSearchParams): Promise<void> {
  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const storedState = sessionStorage.getItem("crux:oauthState");
  const verifier = sessionStorage.getItem(LS_CODE_VERIFIER);

  if (!code) throw new Error("No authorization code in callback");
  if (state !== storedState) throw new Error("OAuth state mismatch");
  if (!verifier) throw new Error("Missing code verifier");

  sessionStorage.removeItem("crux:oauthState");
  sessionStorage.removeItem(LS_CODE_VERIFIER);

  const clientId = localStorage.getItem(LS_CLIENT_ID);
  if (!clientId) throw new Error("No client ID found");

  const redirectUri = `${window.location.origin}/callback`;
  const res = await fetch(`${getServerBase()}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: redirectUri,
      client_id: clientId,
      code_verifier: verifier,
    }),
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed: ${text}`);
  }

  const data = (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
  };
  storeTokens(data);
}

export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  const clientId = localStorage.getItem(LS_CLIENT_ID);
  if (!refreshToken || !clientId) return null;

  const res = await fetch(`${getServerBase()}/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      refresh_token: refreshToken,
      client_id: clientId,
    }),
  });

  if (!res.ok) {
    clearAuth();
    return null;
  }

  const data = (await res.json()) as {
    access_token: string;
    refresh_token?: string;
    expires_in?: number;
  };
  storeTokens(data);
  return data.access_token;
}

export async function getValidToken(): Promise<string | null> {
  const token = getAccessToken();
  if (token) return token;
  return refreshAccessToken();
}
